// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import JettingAppDropDown, {
    JettingAppDropDownStyles,
} from "@stoneage/App/Components/JettingAppComponents/JettingAppDropDown";
import { JetArray } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import React, { useEffect, useState } from "react";

interface Props {
    row: JetArray;
    readOnly: boolean;
    jetOptions: string[];
    tooltipText: string;
    onChange: (value: string) => void;
    extendedStyles?: JettingAppJetRowStyles;
}

export interface JettingAppJetRowStyles {
    wrapper?: InjectableCss;
    dropDown?: JettingAppDropDownStyles;
    angle?: InjectableCss;
    jets?: InjectableCss;
    qty?: InjectableCss;
    size?: InjectableCss;
    flow?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

export const jettingAppJetRowStyles: JettingAppJetRowStyles = {};

const JettingAppJetRow = ({ extendedStyles, onChange, row, jetOptions, readOnly }: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppJetRowStyles, extendedStyles));
    const [dropdownValue, setDropdownValue] = useState(row.jetPart);
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    useEffect(() => {
        if (dropdownValue === row.jetPart) {
            return;
        }
        setDropdownValue(row.jetPart);
    }, [row.jetPart]);

    const onDropDownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        setDropdownValue(value);
        onChange && onChange(value);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.angle} translate="no">
                {row.angle}
            </StyledWrapper>
            {!isTabletScreen && <StyledWrapper {...styles.jets}>{row.label}</StyledWrapper>}
            <StyledWrapper {...styles.qty} translate="no">
                {row.qty}
            </StyledWrapper>
            <StyledWrapper {...styles.size}>
                <JettingAppDropDown
                    readOnly={readOnly}
                    options={jetOptions}
                    label=""
                    value={dropdownValue}
                    onChange={onDropDownChange}
                    optionType="string"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.flow}>
                <TextField translate="no" disabled value={row.flowPercent} />
            </StyledWrapper>
        </StyledWrapper>
    );
};
export default JettingAppJetRow;
